<p-dialog
    [header]="title()"
    [visible]="visibleSignal()"
    (visibleChange)="onVisibleChange($event)"
    [style]="{ width: '53rem' }"
    [breakpoints]="{ '1280px': '95vw' }"
    [modal]="true"
    [closable]="!saving()"
    [draggable]="false"
>
    @if (isEdit()) {
    <!-- Edit: free tab navigation. -->
    <p-tabs
        [value]="activeTab()"
        (valueChange)="activeTab.set($any($event))"
    >
        <p-tablist>
            <p-tab value="general">General</p-tab>
            <p-tab value="baseart">Base Art</p-tab>
            <p-tab value="art">Art</p-tab>
        </p-tablist>
        <p-tabpanels>
            <p-tabpanel value="general">
                <ng-container [ngTemplateOutlet]="generalForm" />
            </p-tabpanel>
            <p-tabpanel value="baseart">
                <app-product-base-art-panel
                    [template]="activeTemplate()"
                    [active]="baseArtActive()"
                />
            </p-tabpanel>
            <p-tabpanel value="art">
                <ng-container [ngTemplateOutlet]="artForm" />
            </p-tabpanel>
        </p-tabpanels>
    </p-tabs>
    } @else {
    <!-- Add: guided wizard. The template is created after the General step so
         the later steps have a template id to attach art to. -->
    <p-stepper [value]="step()" class="wizard-stepper">
        <p-step-list>
            <p-step [value]="1">General</p-step>
            <p-step [value]="2">Base Art</p-step>
            <p-step [value]="3">Art</p-step>
        </p-step-list>
        <p-step-panels>
            <p-step-panel [value]="1">
                <ng-template #content>
                    <ng-container [ngTemplateOutlet]="generalForm" />
                    @if (saveError()) {
                    <p-message
                        severity="error"
                        [text]="saveError()!"
                        styleClass="mt-2"
                    />
                    }
                    <div class="flex justify-end gap-2 pt-3">
                        <p-button
                            label="Cancel"
                            severity="secondary"
                            outlined
                            [disabled]="saving()"
                            (onClick)="close()"
                        />
                        <p-button
                            [label]="
                                activeTemplate()
                                    ? 'Continue'
                                    : 'Create & continue'
                            "
                            icon="pi pi-arrow-right"
                            iconPos="right"
                            [loading]="saving()"
                            [disabled]="!canSaveGeneral()"
                            (onClick)="createAndContinue()"
                        />
                    </div>
                </ng-template>
            </p-step-panel>
            <p-step-panel [value]="2">
                <ng-template #content>
                    <app-product-base-art-panel
                        [template]="activeTemplate()"
                        [active]="baseArtActive()"
                    />
                    <div class="flex justify-between gap-2 pt-3">
                        <p-button
                            label="Back"
                            icon="pi pi-arrow-left"
                            severity="secondary"
                            outlined
                            (onClick)="step.set(1)"
                        />
                        <p-button
                            label="Next"
                            icon="pi pi-arrow-right"
                            iconPos="right"
                            (onClick)="step.set(3)"
                        />
                    </div>
                </ng-template>
            </p-step-panel>
            <p-step-panel [value]="3">
                <ng-template #content>
                    <ng-container [ngTemplateOutlet]="artForm" />
                    @if (saveError()) {
                    <p-message
                        severity="error"
                        [text]="saveError()!"
                        styleClass="mt-2"
                    />
                    }
                    <div class="flex justify-between gap-2 pt-3">
                        <p-button
                            label="Back"
                            icon="pi pi-arrow-left"
                            severity="secondary"
                            outlined
                            [disabled]="saving()"
                            (onClick)="step.set(2)"
                        />
                        <p-button
                            label="Finish"
                            icon="pi pi-check"
                            [loading]="saving()"
                            [disabled]="!canSaveArt()"
                            (onClick)="finishAdd()"
                        />
                    </div>
                </ng-template>
            </p-step-panel>
        </p-step-panels>
    </p-stepper>
    }

    @if (isEdit()) {
    <ng-template #footer>
        <div class="flex flex-col items-end gap-2">
            @if (saveError()) {
            <p-message severity="error" [text]="saveError()!" />
            }
            <div class="flex justify-end gap-2">
                <p-button
                    label="Cancel"
                    severity="secondary"
                    outlined
                    [disabled]="saving()"
                    (onClick)="close()"
                />
                <p-button
                    label="Save"
                    icon="pi pi-check"
                    [loading]="saving()"
                    [disabled]="!canSave()"
                    (onClick)="save()"
                />
            </div>
        </div>
    </ng-template>
    }
</p-dialog>

<!-- ── Shared field groups, projected into a tab (edit) or a step (add) ── -->

<ng-template #generalForm>
    <div class="flex flex-col gap-3 pt-2">
        <!-- Active -->
        <div class="flex flex-col gap-2">
            <label class="font-medium">Active</label>
            <div class="flex items-center gap-3">
                <p-checkbox
                    inputId="tpl-active"
                    [binary]="true"
                    [ngModel]="generalDraft().IsActive"
                    (ngModelChange)="setGeneral('IsActive', $event)"
                />
                <label for="tpl-active" class="text-sm">{{
                    generalDraft().IsActive ? 'Active' : 'Inactive'
                }}</label>
            </div>
        </div>

        <!-- Title -->
        <div
            class="flex flex-col gap-2 pt-2 border-t border-surface-200 dark:border-surface-700"
        >
            <label class="font-medium"
                >Title <span class="text-red-500">*</span></label
            >
            <input
                pInputText
                [pAutoFocus]="true"
                [ngModel]="generalDraft().Title"
                (ngModelChange)="setGeneral('Title', $event ?? '')"
                placeholder="My Game"
            />
        </div>

        <!-- Description -->
        <div
            class="flex flex-col gap-2 pt-2 border-t border-surface-200 dark:border-surface-700"
        >
            <label class="font-medium">Description</label>
            <textarea
                pTextarea
                rows="5"
                [ngModel]="generalDraft().Description"
                (ngModelChange)="setGeneral('Description', $event ?? '')"
            ></textarea>
        </div>

        <!-- Featured -->
        <div
            class="flex flex-col gap-2 pt-2 border-t border-surface-200 dark:border-surface-700"
        >
            <label class="font-medium">Featured</label>
            <div class="flex items-center gap-3">
                <p-checkbox
                    inputId="tpl-featured"
                    [binary]="true"
                    [ngModel]="generalDraft().IsFeatured"
                    (ngModelChange)="setGeneral('IsFeatured', $event)"
                />
                <label for="tpl-featured" class="text-sm">{{
                    generalDraft().IsFeatured ? 'Yes' : 'No'
                }}</label>
            </div>
        </div>

        <!-- Game URL (fixed per environment) -->
        <div
            class="flex flex-col gap-2 pt-2 border-t border-surface-200 dark:border-surface-700"
        >
            <label class="font-medium">Game URL</label>
            <input pInputText [value]="gameUrl" readonly [disabled]="true" />
            <small class="text-xs text-muted-color">
                Fixed for this environment; the games app routes to the right
                game by its game kind.
            </small>
        </div>

        <!-- Game Kind -->
        <div
            class="flex flex-col gap-2 pt-2 border-t border-surface-200 dark:border-surface-700"
        >
            <label class="font-medium"
                >Game Kind <span class="text-red-500">*</span></label
            >
            <p-select
                [options]="gameKindOptions()"
                optionLabel="label"
                optionValue="value"
                [ngModel]="generalDraft().GameKindId"
                (ngModelChange)="setGeneral('GameKindId', $event ?? '')"
                [loading]="templatesService.gameKindsLoading()"
                [disabled]="gameKindLocked()"
                placeholder="Select game kind"
                [filter]="true"
                filterBy="label"
                appendTo="body"
                [style]="{ width: '100%' }"
            />
            @if (gameKindLocked()) {
            <small class="text-xs text-muted-color">
                Game Kind is fixed once a template is created.
            </small>
            }
        </div>

        <!-- Min entry fee -->
        <div
            class="flex flex-col gap-2 pt-2 border-t border-surface-200 dark:border-surface-700"
        >
            <div class="flex items-center justify-between">
                <label class="font-medium">Minimum entry fee</label>
                <div class="flex items-center gap-2">
                    <p-checkbox
                        inputId="tpl-has-min-fee"
                        [binary]="true"
                        [ngModel]="generalDraft().HasMinEntryFee"
                        (ngModelChange)="setGeneral('HasMinEntryFee', $event)"
                    />
                    <label for="tpl-has-min-fee" class="text-sm">Set</label>
                </div>
            </div>
            @if (generalDraft().HasMinEntryFee) {
            <p-inputnumber
                [ngModel]="generalDraft().MinEntryFee"
                (ngModelChange)="setGeneral('MinEntryFee', $event)"
                [min]="0"
                [showButtons]="false"
                placeholder="Minimum entry fee"
            />
            } @else {
            <div class="text-xs text-muted-color italic">
                No minimum entry fee.
            </div>
            }
        </div>

        <!-- Tags -->
        <div
            class="flex flex-col gap-2 pt-2 border-t border-surface-200 dark:border-surface-700"
        >
            <label class="font-medium">Tags</label>
            <input
                pInputText
                [ngModel]="generalDraft().TagsText"
                (ngModelChange)="setGeneral('TagsText', $event ?? '')"
                placeholder="e.g. fun, addictive, classic"
            />
            <small class="text-xs text-muted-color">
                Comma-separated keywords. Used to prefill tags when generating
                reviews for this game.
            </small>
        </div>
    </div>
</ng-template>

<ng-template #artForm>
    <div class="flex flex-col gap-4 pt-2">
        @if (canUpload() && activeTemplate()) {
        <div class="flex justify-end">
            <p-button
                label="Generate icon + banner with AI"
                icon="pi pi-microchip-ai"
                size="small"
                severity="secondary"
                outlined
                (onClick)="openAiArt()"
            />
        </div>
        }

        <!-- Icon -->
        <div class="flex flex-col gap-2">
            <div class="flex items-center justify-between gap-2">
                <label class="font-medium">Icon</label>
                @if (canUpload() && activeTemplate()) {
                <p-button
                    label="Upload"
                    icon="pi pi-upload"
                    size="small"
                    severity="secondary"
                    text
                    [loading]="iconUploading()"
                    (onClick)="iconFile.click()"
                />
                <input
                    #iconFile
                    type="file"
                    accept="image/*"
                    class="hidden"
                    (change)="uploadIcon($event.target)"
                />
                }
            </div>
            <div class="flex items-start gap-3">
                <div
                    class="w-20 h-20 flex-shrink-0 rounded border border-surface-200 dark:border-surface-700 flex items-center justify-center overflow-hidden bg-surface-50 dark:bg-surface-800"
                >
                    @if (artDraft().IconUrl) {
                    <img
                        [src]="resolveGameContentUrl(artDraft().IconUrl)"
                        alt="Icon preview"
                        class="w-full h-full object-cover cursor-pointer"
                        title="View large"
                        (click)="
                            preview.open(
                                resolveGameContentUrl(artDraft().IconUrl),
                                'Icon'
                            )
                        "
                    />
                    } @else {
                    <i class="pi pi-image text-muted-color text-xl"></i>
                    }
                </div>
                <div class="flex-1 min-w-0 flex flex-col gap-1">
                    @if (artDraft().IconUrl; as url) {
                    <div class="flex items-center gap-1 min-w-0">
                        <code
                            class="text-xs text-muted-color truncate"
                            [title]="url"
                            >{{ url }}</code
                        >
                        @if (canUpload() && activeTemplate()) {
                        <p-button
                            icon="pi pi-times"
                            severity="secondary"
                            [text]="true"
                            [rounded]="true"
                            size="small"
                            title="Clear"
                            (click)="setArt('IconUrl', '')"
                        />
                        }
                    </div>
                    } @else {
                    <span class="text-sm text-muted-color italic">Not set</span>
                    }
                    <small class="text-xs text-muted-color">
                        Square, ≥512px. Set it via <b>Upload</b> or
                        <b>Generate with AI</b>.
                    </small>
                </div>
            </div>
        </div>

        <!-- Banner -->
        <div
            class="flex flex-col gap-2 pt-2 border-t border-surface-200 dark:border-surface-700"
        >
            <div class="flex items-center justify-between gap-2">
                <label class="font-medium">Banner</label>
                @if (canUpload() && activeTemplate()) {
                <p-button
                    label="Upload"
                    icon="pi pi-upload"
                    size="small"
                    severity="secondary"
                    text
                    [loading]="bannerUploading()"
                    (onClick)="bannerFile.click()"
                />
                <input
                    #bannerFile
                    type="file"
                    accept="image/*"
                    class="hidden"
                    (change)="uploadBanner($event.target)"
                />
                }
            </div>
            <div
                class="aspect-video w-full max-w-md rounded border border-surface-200 dark:border-surface-700 flex items-center justify-center overflow-hidden bg-surface-50 dark:bg-surface-800"
            >
                @if (artDraft().BannerUrl) {
                <img
                    [src]="resolveGameContentUrl(artDraft().BannerUrl)"
                    alt="Banner preview"
                    class="w-full h-full object-cover cursor-pointer"
                    title="View large"
                    (click)="
                        preview.open(
                            resolveGameContentUrl(artDraft().BannerUrl),
                            'Banner'
                        )
                    "
                />
                } @else {
                <i class="pi pi-image text-muted-color text-2xl"></i>
                }
            </div>
            @if (artDraft().BannerUrl; as url) {
            <div class="flex items-center gap-1 min-w-0 max-w-md">
                <code
                    class="text-xs text-muted-color truncate"
                    [title]="url"
                    >{{ url }}</code
                >
                @if (canUpload() && activeTemplate()) {
                <p-button
                    icon="pi pi-times"
                    severity="secondary"
                    [text]="true"
                    [rounded]="true"
                    size="small"
                    title="Clear"
                    (click)="setArt('BannerUrl', '')"
                />
                }
            </div>
            } @else {
            <span class="text-sm text-muted-color italic">Not set</span>
            }
            <small class="text-xs text-muted-color">
                16:9, at least 1280×720 pixels. Set it via <b>Upload</b> or
                <b>Generate with AI</b>.
            </small>
        </div>

        <!-- Images -->
        <div
            class="flex flex-col gap-2 pt-2 border-t border-surface-200 dark:border-surface-700"
        >
            <div class="flex items-center justify-between gap-2">
                <label class="font-medium"
                    >Images
                    <span class="text-xs text-muted-color font-normal"
                        >(portrait, shown on game detail pages)</span
                    ></label
                >
                @if (canUpload() && activeTemplate()) {
                <div class="flex items-center gap-1">
                    <p-button
                        label="Generate with AI"
                        icon="pi pi-microchip-ai"
                        size="small"
                        severity="secondary"
                        text
                        (onClick)="openAiPages()"
                    />
                    <p-button
                        label="Upload image"
                        icon="pi pi-upload"
                        size="small"
                        severity="secondary"
                        text
                        [loading]="imageUploading()"
                        (onClick)="imageFile.click()"
                    />
                    <input
                        #imageFile
                        type="file"
                        accept="image/*"
                        class="hidden"
                        (change)="uploadImage($event.target)"
                    />
                </div>
                }
            </div>
            @if (imagesArray().length > 0) {
            <div class="flex flex-wrap gap-2">
                @for (img of imagesArray(); track img) {
                <div class="relative">
                    <img
                        [src]="resolveGameContentUrl(img)"
                        [alt]="'Image'"
                        class="w-16 h-28 rounded border border-surface-200 dark:border-surface-700 object-cover bg-surface-50 dark:bg-surface-800 cursor-pointer"
                        title="View large"
                        (click)="
                            preview.open(resolveGameContentUrl(img), 'Image')
                        "
                    />
                    @if (canUpload() && activeTemplate()) {
                    <i
                        class="pi pi-times absolute top-1 right-1 bg-black/50 text-white rounded-full p-1 text-xs cursor-pointer"
                        title="Remove"
                        (click)="removeImage(img); $event.stopPropagation()"
                    ></i>
                    }
                </div>
                }
            </div>
            } @else {
            <span class="text-sm text-muted-color italic">No images.</span>
            }
            <small class="text-xs text-muted-color">
                Portrait (~9:16), used as gallery on the game detail page. Set
                via <b>Upload</b> or <b>Generate with AI</b>.
            </small>
        </div>
    </div>
</ng-template>

<app-ai-finalize-game-art
    [visible]="aiArtVisible()"
    (visibleChange)="aiArtVisible.set($event)"
    [template]="activeTemplate()"
    (applied)="onAiArtApplied($event)"
/>

<app-ai-pages-generate
    [visible]="aiPagesVisible()"
    (visibleChange)="aiPagesVisible.set($event)"
    [template]="activeTemplate()"
    (applied)="onAiPagesApplied($event)"
/>
